import { BackgroundMotion } from "./background-motion";
import { startPendingThoughts } from "./pending-thoughts";
import { useCaptainCameo } from "./use-captain-cameo";
import { startCameoHostReaction } from "./cameo-host-reaction";
import type { CameoHostReaction } from "./prepared-cameos";
import { loadStageCharacterBindings, prepareStageLine, loadStageAudio } from "./stage-character-package";
import { drawStaticCharacterFallback } from "./static-character-fallback";
import characterMotion from "../character-motion.json";
import { useCastMotion } from "./use-cast-motion";
import { useRecordingPositions } from "./use-recording-positions";
import { useRecordingBackground } from "./use-recording-background";
import { FormEvent, type CSSProperties, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  FullCharacterAmbientController,
  FullCharacterBlinkController,
  FullCharacterCellRenderer,
  FullCharacterPerformanceCoordinator,
} from "@sprite-runtime";
import {
  AvailabilitySnapshot,
  ClientPrincipal,
  EpisodeLine,
  EpisodeManifest,
  InteractionAllowance,
  InteractionStatus,
  SafeFailure,
  captureTip,
  completeDailyShare,
  createTip,
  getAvailability,
  getEpisode,
  getInteractionAllowance,
  getPrincipal,
  pollInteraction,
  recordEpisodeCompletedView,
  stageFetch,
  submitInteraction,
  trackStageMetric,
  type NewsDevelopmentNotice,
  type NewsReportingBasis,
} from "./stage-api";
import { studioOutroPlayback } from "./studio-outro";
import { StageCaption, type CaptionPlayback } from "./stage-caption";
import { cameoUsesReducedMotion } from "./cameo-flight.ts";
import { captainReminderEligible } from "./captain-reminder.ts";
import { pendingCaptionPlayback, playReadableCaption } from "./caption-playback";
import { PerformanceClock } from "./performance-clock";
import { EditionVisualScreen } from "./edition-visual-screen";
import { validatedVisuals } from "./edition-visuals";
import { editionResourceLinks } from "./edition-resource-links";
import { backdropTheme } from "./stage-backdrop";
import { useModalDialog } from "./use-modal-dialog";
import { editionBriefFor, isPastEdition, selectStageEpisode, stagePresentation, type StageSelectionMode } from "./stage-presentation";
import { developmentNoticeFor, developmentNoticeAsOf, noticeAwareStoryCard } from "./news-development-notice";
import { reportingBasisFor, reportingBasisAsOf } from "./news-reporting-basis";
import { isAcceptedTipAmount, parseTipAmount } from "./tip-amount";

type CharacterKey = "winston" | "asher";
type RendererMap = Record<CharacterKey, FullCharacterCellRenderer>;
type ClipPlayerMap = Record<CharacterKey, FullCharacterPerformanceCoordinator>;

const processingReplyLabels = [
  "STAND BY FOR THEIR REPLY...", "THEY'RE THINKING IT OVER...",
  "A REPLY IS TAKING SHAPE...", "WINSTON'S OPTIMISTIC. ASHER'S EDITING...",
  "PLEASE ENJOY THIS THOUGHTFUL PAUSE...",
] as const;
function chooseProcessingReplyLabel(previous: string) {
  const choices = processingReplyLabels.filter(label => label !== previous);
  return choices[Math.floor(Math.random() * choices.length)] ?? processingReplyLabels[0];
}

const hardFallback: EpisodeManifest = {
  id: "the-loading-lamp",
  title: "The Loading Lamp",
  slot: "encore",
  publishedAt: new Date(0).toISOString(),
  validUntil: new Date(8640000000000000).toISOString(),
  backgroundUrl: "theme:newsroom",

  sources: [],
  lines: [
    {
      speaker: "winston",
      text: "Our news machine is thinking very hard.",
      durationMs: 2300,
      timeline: [],
      gestures: [{ atMs: 520, durationMs: 850, pose: "emphasize" }],
    },
    {
      speaker: "asher",
      text: "It has one light. The light is doing most of the work.",
      durationMs: 3100,
      timeline: [],
      gestures: [{ atMs: 1450, durationMs: 850, pose: "side_eye" }],
    },
    {
      speaker: "winston",
      text: "Excellent light, though.",
      durationMs: 1700,
      timeline: [],
      gestures: [{ atMs: 400, durationMs: 700, pose: "head_tilt" }],
    },
  ],
};

const hardCareFallback: EpisodeManifest = {
  id: "care-you-matter-embedded",
  title: "You Matter More",
  slot: "care",
  publishedAt: new Date(0).toISOString(),
  validUntil: new Date(8640000000000000).toISOString(),
  backgroundUrl: "theme:newsroom",

  sources: [],
  lines: [
    {
      speaker: "winston",
      text: "Hey. The jokes can wait. You matter more than this show.",
      durationMs: 4200,
      timeline: [],
      gestures: [],
    },
    {
      speaker: "asher",
      text: "Please tell a trusted person who can stay with you, or contact a crisis counselor now.",
      durationMs: 6000,
      timeline: [],
      gestures: [],
    },
    {
      speaker: "winston",
      text: "If you might be in immediate danger, call your local emergency number.",
      durationMs: 5200,
      timeline: [],
      gestures: [],
    },
  ],
};

const isCarePerformance = (item: EpisodeManifest) =>
  item.slot === "care" || item.id.includes("care");

const preserveCurrentBackdrop = (
  current: EpisodeManifest,
  incoming: EpisodeManifest,
): EpisodeManifest => ({
  ...incoming,
  backgroundUrl: current.backgroundUrl ?? incoming.backgroundUrl,
  backgroundTheme: current.backgroundTheme ?? incoming.backgroundTheme,
  backgroundFocusX: current.backgroundFocusX ?? incoming.backgroundFocusX,
  backgroundFocusY: current.backgroundFocusY ?? incoming.backgroundFocusY,
});

const cueCopy: Record<string, string> = {
  encore: "Fresh reel delayed. Deploying a certified awkward encore.",
  "newsroom-break": "The newsroom stepped out to argue with a loading bar.",
  "voice-rest": "The cast is observing a very brief vow of silence.",
  "line-busy": "The audience line is busy doing vocal warmups.",
  handshake: "That message did not pass the secret studio handshake.",
  "tip-jar-closed": "The tip jar is wearing its tiny CLOSED hat.",
  care: "The joke can wait. You matter more.",
  offline: "The internet stepped out. The local cast did not.",
  "provider-rest": "The new-story machine is taking a dignified snack break.",
  "script-mixup": "The script pages are briefly exploring nonlinear storytelling.",
  maintenance: "The stage is receiving a very important tune-up.",
  silent: "A tasteful dramatic pause has been scheduled.",
};

interface StageAppProps {
  legalDockAction?: ReactNode;
}

function NewsDisclosures({ notice, basis }: { notice: NewsDevelopmentNotice | null; basis: NewsReportingBasis | null }) {
  return <>
    {notice && <aside className="story-development-details" aria-label="Developing story details">
      <strong>DEVELOPING STORY</strong>
      <p>{notice.text}</p>
      <p>Reporting as of <time dateTime={notice.asOfUtc}>{developmentNoticeAsOf(notice)}</time>.</p>
    </aside>}
    {basis && <aside className="story-reporting-details" aria-label="Reporting basis">
      <strong>REPORTING NOTE</strong>
      <p>{basis.kind === "trusted-single-source"
        ? "This episode attributes its central development to reporting by "
        : "This episode uses claim-specific reporting, including "}
        <a href={basis.primarySourceUrl} target="_blank" rel="external noopener noreferrer">{basis.publisherLabel}</a>.</p>
      <p>Reporting reviewed as of <time dateTime={basis.asOfUtc}>{reportingBasisAsOf(basis)}</time>.</p>
    </aside>}
  </>;
}

export function StageApp({ legalDockAction }: StageAppProps = {}) {
  const winstonCanvas = useRef<HTMLCanvasElement>(null);
  const asherCanvas = useRef<HTMLCanvasElement>(null);
  const winstonActor = useRef<HTMLDivElement>(null);
  const asherActor = useRef<HTMLDivElement>(null);
  const cameoStage = useRef<HTMLDivElement>(null);
  const stageShell = useRef<HTMLElement>(null);
  const stageNotices = useRef<HTMLDivElement>(null);
  const captionDeck = useRef<HTMLDivElement>(null);
  const arrangeButton = useRef<HTMLButtonElement>(null);
  const arrangementDone = useRef<HTMLButtonElement>(null);
  const playButton = useRef<HTMLButtonElement>(null);
  const audienceButton = useRef<HTMLButtonElement>(null);
  const audienceSheet = useRef<HTMLElement>(null);
  const socialSheet = useRef<HTMLElement>(null);
  const storySheet = useRef<HTMLElement>(null);
  const careSheet = useRef<HTMLElement>(null);
  const followButton = useRef<HTMLButtonElement>(null);
  const audienceReturnFocus = useRef<HTMLElement | null>(null);
  const reportingDialog = useRef<HTMLDialogElement>(null);
  const reportingButton = useRef<HTMLButtonElement>(null);
  const renderers = useRef<RendererMap | null>(null);
  const blinks = useRef<FullCharacterBlinkController[]>([]);
  const ambient = useRef<FullCharacterAmbientController | null>(null);
  const reactionPlayers = useRef<ClipPlayerMap | null>(null);
  const stopCameoHostReaction = useRef<(() => void) | null>(null);
  const speechPlayers = useRef<Record<CharacterKey, FullCharacterPerformanceCoordinator> | null>(null);
  const playbackAbort = useRef<AbortController | null>(null);
  const playbackGeneration = useRef(0);
  const playbackLock = useRef(false);
  const performanceClock = useRef<PerformanceClock | null>(null);
  const scheduledTimers = useRef<number[]>([]);
  const qaCuePlayed = useRef(false);

  const [availability, setAvailability] = useState<AvailabilitySnapshot | null>(null);
  const [episode, setEpisode] = useState<EpisodeManifest>(hardFallback);
  const [featuredEpisode, setFeaturedEpisode] = useState<EpisodeManifest>(hardFallback);
  const [selectionMode, setSelectionMode] = useState<StageSelectionMode>("fallback");
  const [viewingPastEdition, setViewingPastEdition] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [fallbackEpisodes, setFallbackEpisodes] = useState<EpisodeManifest[]>([hardFallback]);
  const [fallbackReady, setFallbackReady] = useState(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [completedEpisodeId, setCompletedEpisodeId] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [audioFailure, setAudioFailure] = useState<string | null>(null);
  const [speaker, setSpeaker] = useState<CharacterKey | null>(null);
  const [subtitle, setSubtitle] = useState("The cast is finding their marks.");
  const [captionPlayback, setCaptionPlayback] = useState<CaptionPlayback | null>(null);
  const [captionsVisible, setCaptionsVisible] = useState(false);
  const [hideRecordingControls, setHideRecordingControls] = useState(true);
  const [recordingControlsRevealed, setRecordingControlsRevealed] = useState(false);
  const [recordingArranging, setRecordingArranging] = useState(false);
  const [recordingTarget, setRecordingTarget] = useState("characters");
  const [recordingViewerCustom, setRecordingViewerCustom] = useState(false);
  const [recordingViewerSize, setRecordingViewerSize] = useState(50);
  const [recordingPreviewIndex, setRecordingPreviewIndex] = useState(0);
  const [recordingScript, setRecordingScript] = useState(() =>
    new URLSearchParams(window.location.search).get("performer") === "captain" ? "captain" : "edition");
  const [recordingCaptainScript, setRecordingCaptainScript] = useState("");
  const [recordingCaptainSize, setRecordingCaptainSize] = useState(100);
  const [recordingBackgroundStyle, setRecordingBackgroundStyle] = useState(() =>
    new URLSearchParams(window.location.search).get("background") === "green" ? "green" : "scene");
  const [recordingCaptainPlaying, setRecordingCaptainPlaying] = useState(false);
  const recordingCaptainGeneration = useRef(0);
  const [notice, setNotice] = useState("TUNING SIGNAL");
  const [theme, setTheme] = useState("newsroom");
  const [loadedBackdropUrl, setLoadedBackdropUrl] = useState<string | null>(null);
  const [activeBackdropUrl, setActiveBackdropUrl] = useState<string | null>(null);
  const [effect, setEffect] = useState<string | null>(null);
  const [principal, setPrincipal] = useState<ClientPrincipal | null>(null);
  const [viewerText, setViewerText] = useState("");
  const [allowance, setAllowance] = useState<InteractionAllowance | null>(null);
  const [ageAttested, setAgeAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [interactionPending, setInteractionPending] = useState(false);
  const [processingReplyLabel, setProcessingReplyLabel] = useState(
    () => processingReplyLabels[Math.floor(Math.random() * processingReplyLabels.length)] ?? processingReplyLabels[0],
  );
  const [rejected, setRejected] = useState(false);
  const [careOpen, setCareOpen] = useState(false);
  const [audienceOpen, setAudienceOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(
    () => new URLSearchParams(window.location.search).get("support") === "tip",
  );
  const tipSheet = useRef<HTMLElement>(null);
  const [studioCtaVisible, setStudioCtaVisible] = useState(false);
  const lastSpokenOutroAt = useRef<number | null>(null);
  const tipOpener = useRef<HTMLElement | null>(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [failedStoryImageBinding, setFailedStoryImageBinding] = useState<string | null>(null);
  const [shareLabel, setShareLabel] = useState("SHARE");
  const [socialsOpen, setSocialsOpen] = useState(false);
  const [tipStatus, setTipStatus] = useState("");
  const [messageStatus, setMessageStatus] = useState("");
  const [tipSubmitting, setTipSubmitting] = useState(false);
  const [customTipOpen, setCustomTipOpen] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState("");
  const [customTipError, setCustomTipError] = useState("");
  const customTipInput = useRef<HTMLInputElement>(null);
  useEffect(() => { if (customTipOpen && tipOpen) customTipInput.current?.focus(); }, [customTipOpen, tipOpen]);
  const tipSubmissionPending = useRef(false);
  const tipCaptureStarted = useRef(false);
  const storyCardButton = useRef<HTMLButtonElement>(null);
  const transcriptDialog = useRef<HTMLDialogElement>(null);
  const transcriptButton = useRef<HTMLButtonElement>(null);
  const storyReturnFocus = useRef<HTMLElement | null>(null);
  const refreshAudienceAllowance = useCallback(async () => {
    if (!principal) {
      setAllowance(null);
      return;
    }
    try {
      setAllowance(await getInteractionAllowance());
    } catch {
      setAllowance(null);
    }
  }, [principal]);
  const resolveBackdropUrl = useCallback((value?: string) => {
    if (typeof value === "string" && value.startsWith("/api/backdrop/")) {
      const token = value.slice("/api/backdrop/".length);
      if (/^[A-Za-z0-9_-]+$/.test(token)) return value;
    }
    return safeBackdropUrl(value);
  }, []);
  const qaBackdropUrl = useMemo(
    () => import.meta.env.DEV
      ? new URLSearchParams(window.location.search).get("qaBackdrop") ?? undefined
      : undefined,
    [],
  );
  const generatedBackdropUrl = useMemo(
    () => resolveBackdropUrl(qaBackdropUrl ?? featuredEpisode.backgroundUrl),
    [featuredEpisode.backgroundUrl, qaBackdropUrl, resolveBackdropUrl],
  );
  const recordMode = useMemo(() => {
    const value = new URLSearchParams(window.location.search).get("record");
    return value === "portrait" || value === "square" || value === "landscape" ? value : null;
  }, []);
  const recordCaptain = !!recordMode && recordingScript === "captain";
  const recordingGreenScreen = !!recordMode && recordingBackgroundStyle === "green";
  const recordingBusy = playing || recordingCaptainPlaying;
  const recordingChromeHidden = !!recordMode && hideRecordingControls && recordingBusy
    && !paused && !audioFailure && !recordingControlsRevealed;
  const recordingPositions = useRecordingPositions(!!recordMode, recordingBusy, stageShell);
  const recordingArrangementOpen = !!recordMode && !recordingBusy && recordingArranging;
  const recordingInterfaceHidden = recordingChromeHidden || recordingArrangementOpen;
  const recordingBackground = useRecordingBackground(generatedBackdropUrl,
    { x: focusPercent(featuredEpisode.backgroundFocusX, 50), y: focusPercent(featuredEpisode.backgroundFocusY, 40), zoom: 100 },
    recordingArrangementOpen && recordingTarget === "background", stageShell);
  const recordingCues = useMemo(() => featuredEpisode.lines.flatMap(line => validatedVisuals(line)), [featuredEpisode.lines]);
  const recordingPreview = recordingArrangementOpen && recordingTarget === "media"
    ? recordingCues[recordingPreviewIndex] ?? recordingCues[0] : undefined;
  const recordBackgroundPosition = `${recordingBackground.value.x}% ${recordingBackground.value.y}%`;
  const finishRecordingArrangement = () => {
    setRecordingArranging(false);
    requestAnimationFrame(() => arrangeButton.current?.focus({ preventScroll: true }));
  };
  const previewAudienceControls = useMemo(
    () => import.meta.env.DEV
      && new URLSearchParams(window.location.search).get("preview") === "audience-controls",
    [],
  );
  const previewInteractionPending = useMemo(
    () => import.meta.env.DEV
      && new URLSearchParams(window.location.search).get("qaThinking") === "1",
    [],
  );
  const showInteractionPending = interactionPending || previewInteractionPending;
  const previewAllowance = useMemo<InteractionAllowance | null>(() => {
    if (!import.meta.env.DEV) return null;
    const raw = new URLSearchParams(window.location.search).get("qaAllowance");
    if (raw === null) return null;
    const value = Number(raw);
    if (value !== 0 && value !== 1) return null;
    return { used: value === 0 ? 1 : 0, available: 1, remaining: value, shareUnlocked: false };
  }, []);
  const displayedAllowance = previewAllowance ?? allowance;
  const storyCard = useMemo(() => noticeAwareStoryCard(featuredEpisode, storyCardFor(featuredEpisode) ?? undefined), [featuredEpisode]);
  const editionBrief = editionBriefFor(featuredEpisode);
  const editionLinks = editionResourceLinks(featuredEpisode.sources);
  const activeNewsNotice = showInteractionPending ? null : developmentNoticeFor(playing ? episode : featuredEpisode);
  const activeReportingBasis = showInteractionPending ? null : reportingBasisFor(playing ? episode : featuredEpisode);
  const featuredNewsNotice = developmentNoticeFor(featuredEpisode);
  const featuredReportingBasis = reportingBasisFor(featuredEpisode);
  const transcriptNewsNotice = developmentNoticeFor(episode);
  const transcriptReportingBasis = reportingBasisFor(episode);
  const storyCardImageUrl = safeStoryImageUrl(storyCard?.imageUrl);
  // Bind a failure to this edition and image, so an obsolete image callback
  // cannot hide a replacement edition's artwork or prevent its retry.
  const storyImageBinding = `${featuredEpisode.id}/${storyCardImageUrl ?? ""}`;
  const displayedStoryCardImageUrl = failedStoryImageBinding === storyImageBinding ? null : storyCardImageUrl;
  const onStoryImageError = () => setFailedStoryImageBinding(storyImageBinding);
  useEffect(() => { reportingDialog.current?.close(); }, [episode.id, featuredEpisode.id, playing]);
  const openAudience = useCallback((opener: HTMLElement) => {
    audienceReturnFocus.current = opener;
    setSocialsOpen(false);
    setTipOpen(false);
    setAudienceOpen(true);
  }, []);
  const openTip = useCallback((opener: HTMLElement) => {
    tipOpener.current = opener;
    setAudienceOpen(false);
    setSocialsOpen(false);
    setTipOpen(true);
  }, []);
  const closeTip = useCallback(() => {
    setTipOpen(false);
    const opener = tipOpener.current;
    tipOpener.current = null;
    window.requestAnimationFrame(() => {
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
  }, []);
  const closeAudience = useCallback(() => {
    const opener = audienceReturnFocus.current;
    audienceReturnFocus.current = null;
    setAudienceOpen(false);
    window.requestAnimationFrame(() => {
      const target = opener?.isConnected ? opener : audienceButton.current;
      target?.focus({ preventScroll: true });
    });
  }, []);
  const openStory = useCallback((trigger: HTMLElement | null) => {
    storyReturnFocus.current = trigger;
    setSocialsOpen(false);
    setStoryOpen(true);
  }, []);
  const closeStory = useCallback(() => {
    const returnFocus = storyReturnFocus.current;
    setStoryOpen(false);
    window.requestAnimationFrame(() => {
      if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
      else storyCardButton.current?.focus({ preventScroll: true });
    });
  }, []);

  const castMotion = useCastMotion();
  const visualScreen = <EditionVisualScreen playback={playing ? captionPlayback : null} lines={episode.lines}
    recording={!!recordMode} preview={recordingPreview} enabled={playing || !!recordingPreview}
    paused={paused} motionEnabled={castMotion.enabled} />;
  const reducedMotion = !castMotion.enabled;

  const closeSocials = useCallback(() => {
    setSocialsOpen(false);
    window.requestAnimationFrame(() => followButton.current?.focus({ preventScroll: true }));
  }, []);
  const closeCare = useCallback(() => setCareOpen(false), []);
  const modalRef = careOpen ? careSheet : storyOpen ? storySheet : socialsOpen ? socialSheet : tipOpen ? tipSheet : audienceSheet;
  const closeModal = careOpen ? closeCare : storyOpen ? closeStory : socialsOpen ? closeSocials : tipOpen ? closeTip : closeAudience;
  useModalDialog(careOpen || storyOpen || socialsOpen || tipOpen || audienceOpen, modalRef, closeModal);
  useLayoutEffect(() => {
    const notices = stageNotices.current;
    if (!notices) return;
    const measure = () => document.documentElement.style.setProperty("--stage-notice-height", `${notices.getBoundingClientRect().height}px`);
    const observer = new ResizeObserver(measure);
    observer.observe(notices); measure();
    return () => { observer.disconnect(); document.documentElement.style.removeProperty("--stage-notice-height"); };
  }, []);
  useLayoutEffect(() => {
    // The flow-based studio does not position controls from caption measurements.
    // Keep legacy recording coordinates independent of the viewer's caption choice.
    if (!recordMode) return;
    const shell = stageShell.current, deck = captionDeck.current;
    if (!shell || !deck) return;
    const status = deck.querySelector<HTMLElement>(".caption-status");
    const measure = () => {
      const bottom = Number.parseFloat(getComputedStyle(deck).bottom) || 0;
      const box = deck.getBoundingClientRect(), statusBox = status?.getBoundingClientRect();
      const extra = playing && statusBox && statusBox.height > 0 ? Math.max(0, box.top - statusBox.top) : 0;
      const contentReserve = Math.ceil(box.height + bottom + extra);
      shell.style.setProperty("--caption-content-reserve", `${contentReserve}px`);
      shell.style.setProperty("--caption-reserve", `${contentReserve + (playing ? 76 : 20)}px`);
    };
    const observer = new ResizeObserver(measure);
    if (status) observer.observe(status);
    observer.observe(deck); measure();
    return () => { observer.disconnect(); shell.style.removeProperty("--caption-reserve"); shell.style.removeProperty("--caption-content-reserve"); };
  }, [playing, paused, subtitle, audioFailure, recordMode]);

  useEffect(() => {
    void trackStageMetric("page_view", undefined, true);
    const youtube = document.querySelector<HTMLAnchorElement>(".youtube-channel-link");
    const trackYoutube = () => void trackStageMetric("youtube_clicked");
    youtube?.addEventListener("click", trackYoutube);
    return () => youtube?.removeEventListener("click", trackYoutube);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setLoadedBackdropUrl((current) => current === generatedBackdropUrl ? current : null);
      setActiveBackdropUrl((current) => current === generatedBackdropUrl ? current : null);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [generatedBackdropUrl]);

  useEffect(() => {
    if (!playing && generatedBackdropUrl && loadedBackdropUrl === generatedBackdropUrl) {
      const frame = window.requestAnimationFrame(() => setActiveBackdropUrl(generatedBackdropUrl));
      return () => window.cancelAnimationFrame(frame);
    }
    return undefined;
  }, [generatedBackdropUrl, loadedBackdropUrl, playing]);

  const clearScheduled = useCallback(() => {
    for (const timer of scheduledTimers.current) window.clearTimeout(timer);
    scheduledTimers.current = [];
  }, []);

  const startAmbient = useCallback(() => {
    if (!reducedMotion) ambient.current?.start();
  }, [reducedMotion]);

  const cameoReaction = useCallback((active: boolean, reaction?: CameoHostReaction) => {
    stopCameoHostReaction.current?.();
    stopCameoHostReaction.current = null;
    if (active) {
      setSpeaker(null);
      ambient.current?.stop();
      blinks.current.forEach(blink => blink.stop());
      stopCameoHostReaction.current = startCameoHostReaction(reaction, reactionPlayers.current ?? {}, reducedMotion);
    } else {
      if (!reducedMotion) blinks.current.forEach(blink => blink.start());
      startAmbient();
    }
  }, [reducedMotion, startAmbient]);
  const cameoEpisode = playing ? episode : featuredEpisode;
  const cameoReducedMotion = cameoUsesReducedMotion(reducedMotion, {
    development: import.meta.env.DEV,
    hostname: window.location.hostname,
    enabled: import.meta.env.VITE_CAMEO_MOTION_REVIEW === "1",
    followSystem: new URLSearchParams(window.location.search).get("cameoMotion") === "system",
  });
  const captain = useCaptainCameo({
    stage: cameoStage,
    episodeId: cameoEpisode.id,
    eligible: ready && (!recordMode || recordCaptain) && !careOpen && !showInteractionPending &&
      !isCarePerformance(cameoEpisode) && cameoEpisode.id === featuredEpisode.id &&
      (cameoEpisode.sources.length > 0 || import.meta.env.DEV && new URLSearchParams(window.location.search).get("captainPreview") === "1"),
    reducedMotion: cameoReducedMotion, onReaction: cameoReaction, recording: !!recordMode,
  });
  const { boundary: cameoBoundary, cancel: cancelCameo } = captain;
  const selectedCaptainScript = captain.scripts.find(script => script.id === recordingCaptainScript) ?? captain.scripts[0];
  useEffect(() => { setRecordingCaptainScript(""); }, [featuredEpisode.id]);
  const captainReminderReady = captainReminderEligible({
    completedEpisodeId, episodeId: cameoEpisode.id, available: captain.available, comic: captain.comic,
    guestState: captain.state, playing, paused, reducedMotion: cameoReducedMotion, recording: !!recordMode,
    busy: showInteractionPending || careOpen || audienceOpen || tipOpen || storyOpen || socialsOpen,
  });
  useEffect(() => { setCompletedEpisodeId(null); }, [featuredEpisode.id]);
  useEffect(() => {
    if (captain.state === "active") setCaptionsVisible(true);
  }, [captain.state]);

  useEffect(() => {
    let disposed = false;
    void (async () => {
      const snapshot = await getAvailability();
      if (disposed) return;
      setAvailability(snapshot);
      setNotice(snapshot.contentMode === "fresh" ? "TODAY'S TRANSMISSION" : snapshot.contentMode === "encore" ? "ENCORE REEL" : "STUDIO STANDBY");

      const selection = await selectStageEpisode(new URLSearchParams(window.location.search), snapshot.episodeManifestUrl, getEpisode);
      let selected = selection.episode;
      if (selected) {
        void isPastEdition(selected, selection.mode, snapshot.episodeManifestUrl, getEpisode).then((past) => {
          if (!disposed) setViewingPastEdition(past);
        });
      }

      const catalog = await loadFallbackCatalog(snapshot.fallbackCatalogUrl);
      if (disposed) return;
      const safeCatalog = catalog.some(isCarePerformance)
        ? catalog
        : [...catalog, hardCareFallback];
      setFallbackEpisodes(safeCatalog);
      setFallbackReady(true);
      if (!selected) {
        const generalCatalog = safeCatalog.filter((item) => !isCarePerformance(item));
        selected = generalCatalog[Math.floor(Math.random() * generalCatalog.length)] ?? hardFallback;
        setSubtitle(cueCopy[snapshot.fallbackCue ?? "encore"] ?? cueCopy.encore);
      }
      setSelectionMode(selection.episode ? selection.mode : "fallback");
      setSelectionError(selection.error);
      setEpisode(selected);
      setFeaturedEpisode(selected);
      setTheme(backdropTheme(selected));
    })();
    void getPrincipal().then((value) => {
      if (!disposed) setPrincipal(value);
    });
    return () => {
      disposed = true;
    };
  }, []);

  useEffect(() => {
    let disposed = false;
    const abort = new AbortController();
    setReady(false);
    void (async () => {
      try {
        const bindings = await loadStageCharacterBindings(abort.signal);
        const loaded = await Promise.allSettled([
          FullCharacterCellRenderer.load({ canvas: winstonCanvas.current!, binding: bindings.winston, signal: abort.signal }),
          FullCharacterCellRenderer.load({ canvas: asherCanvas.current!, binding: bindings.asher, signal: abort.signal }),
        ]);
        if (disposed || loaded.some(result => result.status === "rejected")) {
          loaded.forEach(result => { if (result.status === "fulfilled") result.value.dispose(); });
          throw new Error("Character package could not be staged.");
        }
        const [winston, asher] = loaded.map(result => (result as PromiseFulfilledResult<FullCharacterCellRenderer>).value);
        renderers.current = { winston, asher };
        speechPlayers.current = { winston: new FullCharacterPerformanceCoordinator(winston), asher: new FullCharacterPerformanceCoordinator(asher) };
        reactionPlayers.current = { winston: new FullCharacterPerformanceCoordinator(winston), asher: new FullCharacterPerformanceCoordinator(asher) };
        blinks.current = [winston, asher].map(renderer => new FullCharacterBlinkController(renderer, {
          seed: renderer.catalog.binding.canonAssetId + "-natural-blink",
          minIntervalMs: 1800, maxIntervalMs: 4200, halfFrameMs: 65, closedFrameMs: 95,
          dependencies: { reducedMotion: () => reducedMotion },
        }));
        if (!reducedMotion) blinks.current.forEach(blink => blink.start());
        ambient.current = new FullCharacterAmbientController([
          { renderer: winston, element: winstonActor.current ?? undefined, idlePerformances: characterMotion.winston },
          { renderer: asher, element: asherActor.current ?? undefined, idlePerformances: characterMotion.asher },
        ], { seed: "awkwardpixels-coming-soon-ambient", minIntervalMs: 1800, maxIntervalMs: 3400,
          dependencies: { reducedMotion: () => reducedMotion } });
        startAmbient();
        setReady(true);
        setSubtitle(current => current === "The cast is finding their marks." ? "The cast has found most of their marks." : current);
      } catch {
        if (disposed) return;
        await Promise.allSettled([
          drawStaticCharacterFallback(winstonCanvas.current!, "/sprites/winston-rest.png", { signal: abort.signal, canvasWidth: 640, canvasHeight: 640 }),
          drawStaticCharacterFallback(asherCanvas.current!, "/sprites/asher-rest.png", { signal: abort.signal, canvasWidth: 640, canvasHeight: 640 }),
        ]);
        if (!disposed) { setReady(true); setAudioFailure("Animation unavailable. The transcript is still available."); }
      }
    })();
    return () => {
      disposed = true; abort.abort(); playbackAbort.current?.abort();
      playbackGeneration.current += 1; performanceClock.current?.stop(); playbackLock.current = false;
      clearScheduled(); blinks.current.forEach(blink => blink.dispose()); ambient.current?.dispose();
      Object.values(reactionPlayers.current ?? {}).forEach(player => player.dispose());
      Object.values(speechPlayers.current ?? {}).forEach(player => player.dispose());
      Object.values(renderers.current ?? {}).forEach(renderer => renderer.dispose());
      renderers.current = null; speechPlayers.current = null; reactionPlayers.current = null;
    };
  }, [clearScheduled, reducedMotion, startAmbient]);

  useEffect(() => {
    if (!showInteractionPending || !ready || !reactionPlayers.current) return;
    const players = reactionPlayers.current;
    ambient.current?.stop();
    const stopThoughts = startPendingThoughts([
      { player: players.winston, blink: blinks.current[0] },
      { player: players.asher, blink: blinks.current[1] },
    ], { reducedMotion });
    return () => {
      stopThoughts();
      renderers.current?.winston.reset();
      renderers.current?.asher.reset();
      if (!playbackLock.current) startAmbient();
    };
  }, [ready, reducedMotion, showInteractionPending, startAmbient]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tipResult = params.get("tip");
    if (tipResult !== "return" && tipResult !== "cancel") return;
    if (tipCaptureStarted.current) return;
    const clearTipReturn = (keepPendingOrder = false) => {
      if (!keepPendingOrder) sessionStorage.removeItem("awkwardpixels-tip-order");
      params.delete("tip");
      params.delete("token");
      params.delete("PayerID");
      const query = params.toString();
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
      );
    };
    const redirectTimer = window.setTimeout(() => {
      if (tipCaptureStarted.current) return;
      tipCaptureStarted.current = true;
      setTipOpen(true);
      if (tipResult === "cancel") {
        setTipStatus("The tip stayed safely in your pocket. The jar will be here if you change your mind.");
        clearTipReturn();
        return;
      }
      const orderId = params.get("token") ?? sessionStorage.getItem("awkwardpixels-tip-order");
      if (!orderId) {
        setTipStatus("The tip jar came back without its claim ticket. Nothing was credited here.");
        clearTipReturn();
        return;
      }
      sessionStorage.setItem("awkwardpixels-tip-order", orderId);
      let pendingReceipt = true;
      void captureTip(orderId)
        .then((receipt) => {
          if (receipt?.status !== "complete") throw new Error("tip-not-confirmed");
          pendingReceipt = false;
          setTipStatus(receipt.audienceUnlocked
            ? "Tip received. You can send Winston and Asher up to three messages today."
            : "Tip received. Thank you for supporting the studio!");
          void refreshAudienceAllowance();
        })
        .catch(() => setTipStatus("The tip jar could not confirm the receipt. Please do not retry the payment yet."))
        .finally(() => clearTipReturn(pendingReceipt));
    }, 0);
    return () => window.clearTimeout(redirectTimer);
  }, [refreshAudienceAllowance]);

  useEffect(() => {
    if (!audienceOpen) return;
    const timer = window.setTimeout(() => void refreshAudienceAllowance(), 0);
    return () => window.clearTimeout(timer);
  }, [audienceOpen, refreshAudienceAllowance]);

  const playLine = useCallback(async (line: EpisodeLine, generation: number, clock: PerformanceClock) => {
    if (!await clock.wait(0) || playbackGeneration.current !== generation) return;
    const signal = playbackAbort.current!.signal;
    const isCurrent = () => playbackGeneration.current === generation && !signal.aborted;
    clearScheduled(); setSpeaker(line.speaker);
    // This callback has crossed an await, outside React render/lifecycle work.
    // Commit text + its own frozen caption clock atomically before audio can
    // start. Clearing timing here briefly exposed the unpaginated full turn.
    flushSync(() => {
      setSubtitle(line.text);
      setCaptionPlayback(pendingCaptionPlayback(line, isCurrent));
    });
    ambient.current?.stop();
    const renderer = renderers.current?.[line.speaker];
    const performer = speechPlayers.current?.[line.speaker];
    const blink = blinks.current[line.speaker === "winston" ? 0 : 1];
    blink?.stop();
    if (!reducedMotion) blinks.current.forEach(other => { if (other !== blink) other.start(); });
    let audio: Awaited<ReturnType<typeof loadStageAudio>> | undefined;
    let lineEnded = false;
    const readableFallback = async () => {
      audio?.audio.pause();
      renderer?.reset();
      if (!isCurrent()) return;
      // Text becomes the primary delivery when audio cannot play. Recording
      // always renders captions; normal viewers otherwise opt in themselves.
      setCaptionsVisible(true);
      setRecordingControlsRevealed(true);
      startAmbient();
      await playReadableCaption(line, clock, isCurrent, setCaptionPlayback);
    };
    try {
      if (renderer && performer) {
        // Dynamic worker receipts bind the accepted package's rest state, not
        // whichever ambient frame happened to be visible before this turn.
        renderer.reset();
        const prepared = prepareStageLine(line, renderer);
        audio = await loadStageAudio(prepared, signal, renderer, reducedMotion);
        if (line.audio && !audio) setAudioFailure("Audio unavailable. The transcript is still available.");
        if (!await clock.wait(0) || signal.aborted) return;
        if (audio) {
          const captionAudio = audio.audio;
          flushSync(() => setCaptionPlayback({ line, audio: captionAudio, startedAt: performance.now(), isCurrent }));
          const performanceTask = performer.play({ audio: audio.audio, durationMs: prepared.durationMs,
            speechFrames: prepared.timeline, gestures: prepared.gestures, expressions: prepared.expressions!,
            blinkSeed: line.speaker + "-" + line.text, blinkStartsMs: prepared.blinkStartsMs, reducedMotion, signal,
            timeoutMs: prepared.durationMs + 1800 });
          startAmbient();
          const result = await performanceTask;
          lineEnded = result === "ended";
          if (result !== "ended" && isCurrent()) {
            setAudioFailure("Audio unavailable. The transcript is still available.");
            await readableFallback();
          }
        } else {
          await readableFallback();
        }
      } else {
        setAudioFailure("Animation unavailable. The transcript is still available.");
        await readableFallback();
      }
    } catch {
      if (!signal.aborted) {
        setAudioFailure("Audio unavailable. The transcript is still available.");
        await readableFallback();
      }
    } finally {
      audio?.dispose();
      if (playbackGeneration.current === generation && !signal.aborted) {
        renderer?.reset(); if (!reducedMotion) blink?.start();
      }
    }
    if (playbackGeneration.current === generation) await clock.wait(170);
    return lineEnded && isCurrent();
  }, [clearScheduled, reducedMotion, startAmbient]);

  const stopPlayback = useCallback(() => {
    recordingCaptainGeneration.current += 1;
    setRecordingCaptainPlaying(false);
    cancelCameo();
    playbackGeneration.current += 1;
    playbackAbort.current?.abort();
    performanceClock.current?.stop();
    performanceClock.current = null;
    ambient.current?.stop();
    blinks.current.forEach(blink => blink.stop());
    Object.values(speechPlayers.current ?? {}).forEach((player) => player.stop());
    Object.values(renderers.current ?? {}).forEach((renderer) => renderer.reset());
    clearScheduled();
    playbackLock.current = false;
    setPlaying(false);
    setPaused(false);
    setSpeaker(null);
    setEffect(null);
    setCaptionPlayback(null);
    setStudioCtaVisible(false);
    setSubtitle("Playback stopped. The transcript is still available.");
    // Clean recording controls are inert until React has committed the stop.
    requestAnimationFrame(() => playButton.current?.focus({ preventScroll: true }));
    if (!reducedMotion) blinks.current.forEach((blink) => blink.start());
    startAmbient();
  }, [cancelCameo, clearScheduled, reducedMotion, startAmbient]);

  useEffect(() => {
    if (!playing && !recordingCaptainPlaying) return;
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || document.querySelector("dialog[open], [data-stage-modal]")) return;
      const transcript = document.querySelector<HTMLDetailsElement>(".transcript-drawer[open]");
      if (transcript) {
        transcript.open = false;
        transcript.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true });
      } else stopPlayback();
      event.preventDefault();
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [playing, recordingCaptainPlaying, stopPlayback]);

  const playCaptainRecording = async () => {
    if (!recordCaptain || !captain.available || !selectedCaptainScript || recordingBusy) return;
    const generation = ++recordingCaptainGeneration.current;
    setAudioFailure(null);
    setRecordingControlsRevealed(false);
    setRecordingCaptainPlaying(true);
    try {
      if (captain.request(false, selectedCaptainScript.id)) await cameoBoundary(featuredEpisode.id);
    } finally {
      if (generation === recordingCaptainGeneration.current) {
        setRecordingCaptainPlaying(false);
        requestAnimationFrame(() => playButton.current?.focus({ preventScroll: true }));
      }
    }
  };

  // A failed media load keeps the readable fallback and Stop control visible.
  useEffect(() => {
    if (recordCaptain && captain.caption?.note) setRecordingControlsRevealed(true);
  }, [recordCaptain, captain.caption?.note]);

  const togglePause = useCallback(() => {
    const clock = performanceClock.current;
    if (!playbackLock.current || !clock) return;
    if (clock.paused) {
      clock.resume();
      Object.values(speechPlayers.current ?? {}).forEach((player) => player.resume());
      if (!reducedMotion) blinks.current.forEach(blink => blink.start());
      startAmbient();
      setPaused(false);
    } else {
      clock.pause();
      Object.values(speechPlayers.current ?? {}).forEach((player) => player.pause());
      ambient.current?.stop();
      blinks.current.forEach(blink => blink.stop());
      setPaused(true);
    }
  }, [reducedMotion, startAmbient]);

  const requestPhoneFullscreen = useCallback(() => {
    const phoneLikeViewport = Math.min(window.innerWidth, window.innerHeight) <= 680;
    const touchInput = window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;
    const standalone = window.matchMedia("(display-mode: standalone)").matches;
    const fullscreenDocument = document as Document & { webkitFullscreenElement?: Element | null };
    if (!phoneLikeViewport || !touchInput || standalone || document.fullscreenElement || fullscreenDocument.webkitFullscreenElement) return;

    const target = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    try {
      const attempt = typeof target.requestFullscreen === "function"
        ? target.requestFullscreen({ navigationUI: "hide" })
        : target.webkitRequestFullscreen?.();
      if (attempt) void Promise.resolve(attempt).catch(() => undefined);
    } catch {
      // Fullscreen is optional; playback must continue on unsupported phones.
    }
  }, []);

  const play = useCallback(async (selected: EpisodeManifest = episode) => {
    if (playbackLock.current || playing || !ready) return;
    cancelCameo();
    playbackLock.current = true;
    const abort = new AbortController();
    playbackAbort.current = abort;
    void trackStageMetric("play_started", selected.id);
    const generation = ++playbackGeneration.current;
    const clock = new PerformanceClock();
    performanceClock.current = clock;
    const selectedEffect = allowedEffect(selected.effect);
    const playbackEffect = selectedEffect === "lights-out" && selected.sources.length > 0
      ? null
      : selectedEffect;
    setEpisode(selected);
    setCompletedEpisodeId(null);
    setStudioCtaVisible(false);
    setPlaying(true);
    setRecordingControlsRevealed(false);
    setPaused(false);
    setAudioFailure(null);
    setEffect(playbackEffect === "rickroll" ? null : playbackEffect);
    setTheme(backdropTheme(selected));
    const backdropEpisode = featuredEpisode.sources.length > 0 ? featuredEpisode : selected;
    const selectedBackdropUrl = resolveBackdropUrl(qaBackdropUrl ?? backdropEpisode.backgroundUrl);
    setActiveBackdropUrl(selectedBackdropUrl);
    ambient.current?.stop();
    blinks.current.forEach((blink) => blink.stop());
    Object.values(reactionPlayers.current ?? {}).forEach((player) => player.stop());
    let allLinesEnded = selected.lines.length > 0;
    try {
      for (const line of selected.lines) {
        if (playbackGeneration.current !== generation) break;
        if (!await playLine(line, generation, clock)) allLinesEnded = false;
        if (playbackGeneration.current === generation) await cameoBoundary(selected.id);
      }
      if (playbackGeneration.current === generation) {
        // The news transcript is finished before studio promotion begins.
        const outro = studioOutroPlayback(selected, availability?.capabilities, lastSpokenOutroAt.current, Date.now());
        setStudioCtaVisible(outro.visible);
        if (outro.lines.length > 0) lastSpokenOutroAt.current = Date.now();
        for (const line of outro.lines) {
          if (playbackGeneration.current !== generation) break;
          if (!await playLine(line, generation, clock)) allLinesEnded = false;
          if (playbackGeneration.current === generation) await cameoBoundary(selected.id);
        }
        if (playbackGeneration.current !== generation) return;
        if (allLinesEnded) setCompletedEpisodeId(selected.id);
        setSpeaker(null);
        setSubtitle(selected.sources.length > 0 ? "Today's tiny transmission has concluded." : "The reel has returned safely to its can.");
        if (playbackEffect === "rickroll") setEffect(playbackEffect);
        if (selected.sources.length > 0) void recordEpisodeCompletedView(selected.id);
      }
    } finally {
      abort.abort();
      clock.stop();
      if (playbackGeneration.current === generation) {
        if (playbackEffect !== "rickroll") setEffect(null);
        setPlaying(false);
        setPaused(false);
        performanceClock.current = null;
        playbackLock.current = false;
        if (!reducedMotion) blinks.current.forEach((blink) => blink.start());
        startAmbient();
      }
    }
  }, [availability, cancelCameo, cameoBoundary, episode, featuredEpisode, playLine, playing, qaBackdropUrl, ready, reducedMotion, resolveBackdropUrl, startAmbient]);

  const playCue = useCallback(async (cue?: string, playImmediately = true) => {
    if (playbackLock.current) return;
    const generation = playbackGeneration.current;
    if (cue === "handshake" && reactionPlayers.current) {
      ambient.current?.stop();
      await Promise.all([
        ...Object.values(reactionPlayers.current).map(player => player.play({
          durationMs: 600, speechFrames: [{ atMs: 0, frame: "rest" }], gestures: [],
          expressions: [{ atMs: 0, durationMs: 600, expression: "concerned" }],
          blinkStartsMs: [], reducedMotion,
        })),
      ]);
      if (generation !== playbackGeneration.current) return;
    }
    const generalFallbacks = fallbackEpisodes.filter((item) => !isCarePerformance(item));
    const matching = fallbackEpisodes.find((item) =>
      item.id.includes(cue ?? "") || item.slot === cue)
      ?? (cue === "care" ? hardCareFallback : null)
      ?? generalFallbacks[Math.floor(Math.random() * generalFallbacks.length)]
      ?? hardFallback;
    setEpisode(matching);
    setStudioCtaVisible(false);
    setNotice(cue === "care" ? "WE CARE ABOUT YOU" : "EMERGENCY COMEDY REEL");
    setSubtitle(cueCopy[cue ?? "encore"] ?? cueCopy.encore);
    if (!playImmediately) return;
    await delay(650);
    if (generation !== playbackGeneration.current) return;
    await play(matching);
  }, [fallbackEpisodes, play]);

  useEffect(() => {
    if (!import.meta.env.DEV || !ready || !fallbackReady || qaCuePlayed.current) return;
    const qaMode = new URLSearchParams(window.location.search).get("qa");
    if (!qaMode?.startsWith("cue-")) return;
    qaCuePlayed.current = true;
    const manualPlayback = new URLSearchParams(window.location.search).get("qaPlayback") === "1";
    const timer = window.setTimeout(
      () => void playCue(qaMode.slice("cue-".length), !manualPlayback),
      0,
    );
    return () => window.clearTimeout(timer);
  }, [fallbackReady, playCue, ready]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const text = viewerText.trim();
    if (isLocalCareConcern(text)) {
      setCareOpen(true);
      await playCue("care");
      return;
    }
    if (!ageAttested || text.length < 1 || text.length > 240) {
      flashRejected();
      return;
    }
    setSubmitting(true);
    setMessageStatus("");
    setProcessingReplyLabel((previous) => chooseProcessingReplyLabel(previous));
    setInteractionPending(true);
    setAudienceOpen(false);
    setNotice("THE TINY STUDIO IS THINKING");
    try {
      let status = await submitInteraction(
        text,
        featuredEpisode.sources.length > 0 ? featuredEpisode.id : undefined,
      );
      if (status.status === "care") {
        setInteractionPending(false);
        setCareOpen(true);
        await playCue("care");
        return;
      }
      if (status.status === "queued" || status.status === "processing" || status.status === "complete") {
        void refreshAudienceAllowance();
      }
      for (let attempt = 0; status.status === "queued" || status.status === "processing"; attempt++) {
        if (attempt >= 24) throw new Error("poll-timeout");
        await delay(2000);
        status = await pollInteraction(status.id);
      }
      setInteractionPending(false);
      await handleInteraction(status);
      if (status.status !== "complete" && status.status !== "care") {
        setMessageStatus("That message could not be answered. Your text is still here; you can revise it.");
        setAudienceOpen(true);
      }
    } catch (error) {
      setInteractionPending(false);
      const failure = (error as Error & { failure?: SafeFailure }).failure;
      if (failure?.status === "daily-turn-used") {
        setAllowance((current) => current ? { ...current, used: current.available, remaining: 0 } : current);
        setSubtitle("Today's message has already had its moment under the studio lights.");
        setMessageStatus("Today's message limit has been reached. Your text is still here.");
      } else {
        setMessageStatus("The studio could not confirm a reply. Your text is still here. Try again later.");
        await playCue(failure?.fallbackCue ?? "line-busy");
      }
      setAudienceOpen(true);
    } finally {
      setInteractionPending(false);
      setSubmitting(false);
    }
  };

  const handleInteraction = async (status: InteractionStatus) => {
    if (status.status === "complete" && status.episode) {
      const audienceEpisode = preserveCurrentBackdrop(episode, status.episode);
      setViewerText("");
      setEpisode(audienceEpisode);
      setStudioCtaVisible(false);
      setNotice("AUDIENCE TRANSMISSION");
      await play(audienceEpisode);
      return;
    }
    if (status.status === "care") {
      setCareOpen(true);
      await playCue("care");
      return;
    }
    if (status.status === "rejected") flashRejected();
    await playCue(status.failure?.fallbackCue ?? "line-busy");
  };

  const flashRejected = () => {
    setRejected(true);
    window.setTimeout(() => setRejected(false), 2800);
  };

  const share = async (studio = false) => {
    void trackStageMetric("share_clicked", featuredEpisode.id);
    let shareUrl = studio ? `${window.location.origin}/` : `${window.location.origin}/?episode=${encodeURIComponent(featuredEpisode.id)}`;
    if (!studio) try {
      const response = await fetch("/api/shares", {
        method: "POST",
        headers: { accept: "application/json", "content-type": "application/json" },
        body: JSON.stringify({ episodeId: featuredEpisode.id }),
      });
      if (response.ok) shareUrl = ((await response.json()) as { url: string }).url;
    } catch {
      // The canonical page remains a safe share target when the expiring-reel service is resting.
    }
    const data = {
      title: "AwkwardPixels Studio",
      text: studio ? "Meet Winston and Asher at AwkwardPixels Studio." : `Winston and Asher just aired “${featuredEpisode.title}.”`,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        setShareLabel("SHARED");
        if (signedIn) {
          try {
            const updatedAllowance = await completeDailyShare();
            setAllowance(updatedAllowance);
            setShareLabel("2 MESSAGES TODAY");
            void trackStageMetric("share_allowance_unlocked", featuredEpisode.id);
          } catch {
            setTipStatus("Your share made it out, but the second message got stage fright. Please do not repeat it just for credit.");
          }
        }
      } else {
        await navigator.clipboard.writeText(`${data.text} ${data.url}`);
        setShareLabel("LINK COPIED");
      }
    } catch {
      setShareLabel("SHARE CANCELLED");
    }
    window.setTimeout(() => setShareLabel("SHARE"), 2200);
  };

  const tip = async (amount: number) => {
    if (tipSubmissionPending.current || !tipEnabled || !isAcceptedTipAmount(amount)) return;
    tipSubmissionPending.current = true;
    setTipSubmitting(true);
    setTipStatus("Opening the tiny accounting department...");
    try {
      const order = await createTip(amount, crypto.randomUUID());
      sessionStorage.setItem("awkwardpixels-tip-order", order.id);
      window.location.assign(order.approvalUrl);
    } catch {
      tipSubmissionPending.current = false;
      setTipSubmitting(false);
      setTipStatus(cueCopy["tip-jar-closed"]);
      await playCue("tip-jar-closed");
    }
  };

  const signedIn = principal?.userRoles.includes("authenticated") ?? false;
  const signInAvailable = availability?.capabilities.signIn ?? false;
  const promptEnabled = availability?.capabilities.personalInput ?? false;
  const effectivePromptEnabled = promptEnabled || previewAudienceControls;
  const tipEnabled = availability?.capabilities.tip ?? false;
  const featuredPresentation = stagePresentation(featuredEpisode, selectionMode, availability?.contentMode);
  const replayStory = featuredPresentation.label?.includes("REPLAY") ?? false;
  const audienceAvailable = (signInAvailable && promptEnabled) || previewAudienceControls;
  const signInHref = `/.auth/login/google?post_login_redirect_uri=${encodeURIComponent(window.location.pathname + window.location.search)}`;
  const presentation = stagePresentation(episode, episode.id === featuredEpisode.id ? selectionMode : "current",
    episode.id === featuredEpisode.id ? availability?.contentMode : undefined);
  const backdropReady = !!activeBackdropUrl && activeBackdropUrl === generatedBackdropUrl &&
    activeBackdropUrl === loadedBackdropUrl;

  const transcriptContent = (
    <div className="transcript-panel">
      <h2>{episode.title}</h2>
      {presentation.details && <p className="episode-presentation">{presentation.details}</p>}
      <NewsDisclosures notice={transcriptNewsNotice} basis={transcriptReportingBasis} />
      {selectionError && <p role="status">{selectionError} <a href="/stories">BROWSE STORIES</a></p>}
      <ol>
        {episode.lines.map((line, index) => (
          <li key={`${line.speaker}-${index}`}>
            <strong>{line.speaker === "asher" ? "ASHER" : "WINSTON"}</strong>
            <span>{line.text}</span>
          </li>
        ))}
      </ol>
       {episode.studioOutro?.version === "studio-outro-v1" && (
         <section aria-label="Studio promotion, separate from the news">
           <h3>STUDIO OUTRO</h3>
           {episode.studioOutro.lines.map((line, index) => <p key={index}>{line.text}</p>)}
         </section>
       )}
      {episode.sources.length > 0 && (
        <section className="transcript-sources" aria-label="Story sources">
          <h3>Sources</h3>
          <ul>{episode.sources.map((source, index) => <li key={`${source.url}-${index}`}><a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a></li>)}</ul>
          <a href={`/api/episodes/${encodeURIComponent(episode.id)}`}>OPEN EPISODE PAGE &#8599;</a>
        </section>
      )}
    </div>
  );
  const legalDock = (
    <nav className="legal-dock" aria-label="Legal and ownership">
      <span>© 2026 INTRIGUED.ME, LLC</span>
      <a href="/characters/">CHARACTERS</a>
      {!recordMode && <a href="/about/">ABOUT</a>}
      {recordMode && <a href="/stories">STORIES</a>}
      <a href="/terms/">TERMS</a>
      <a href="/privacy/">PRIVACY</a>
      {legalDockAction}
    </nav>
  );

  return (
    <main style={recordMode ? { "--recording-background-zoom": recordingBackground.value.zoom / 100,
      "--recording-background-focus": recordBackgroundPosition, "--recording-viewer-size": `${recordingViewerSize}cqw`,
      "--recording-captain-scale": recordingCaptainSize / 100 } as CSSProperties : undefined}
      className={`site theme-${theme} ${effect === "lights-out" ? "lights-out" : ""} ${recordMode ? `record-mode record-${recordMode}` : "studio-player"}`}>
      <header className="masthead">
        <a className="wordmark" href="/" aria-label="AwkwardPixels Studio home">
          <span>AWKWARD</span><i>PIXELS<sup>TM</sup></i><small>.STUDIO</small>
        </a>
        <div className="broadcast-status"><span aria-hidden="true" /> {[featuredPresentation.label ?? notice, audioFailure].filter(Boolean).join(" · ")}</div>
      </header>

      <section className="hero" aria-labelledby="show-title">
        <div className="eyebrow">A TINY DAILY SHOW · AWKWARDPIXELS.STUDIO</div>
        <h2 id="show-title">
          {playing
            ? "Today's tiny broadcast is on air."
            : ready
              ? "Winston and Asher are ready."
              : "The stage is warming up."}
        </h2>
        <p className="lede">One upbeat story. Two very different dogs. Press play.</p>
      </section>

      <section ref={stageShell} className={`stage-shell ${playing ? "is-playing" : ""} ${studioCtaVisible ? "has-studio-cta" : ""} ${effect === "confetti" ? "confetti" : ""} ${effect === "weather" ? "weather-effect" : ""} ${backdropReady ? "has-story-backdrop" : ""}`} aria-label="AwkwardPixels live stage preview"
        data-recording-clean={recordingChromeHidden ? "true" : undefined}
        data-recording-performer={recordCaptain ? "captain" : undefined}
        data-recording-background={recordingGreenScreen ? "green" : undefined}
        data-recording-active={recordingCaptainPlaying ? "true" : undefined}
        data-recording-arranging={recordingArrangementOpen ? "true" : undefined}
        data-recording-target={recordingArrangementOpen ? recordingTarget : undefined}
        onKeyDown={event => { if (recordingArrangementOpen && event.key === "Escape") { event.preventDefault(); finishRecordingArrangement(); } }}
        onPointerDown={() => { if (recordingChromeHidden) setRecordingControlsRevealed(true); }}>
        {recordingArrangementOpen && <div className="recording-arrangement-tools" role="group" aria-label="Arrange recording scene">
          <label>Move <select aria-label="Arrange target" value={recordingTarget} onChange={event => {
            setRecordingTarget(event.target.value); if (event.target.value === "media") setRecordingViewerCustom(true);
          }}><option value="characters">Characters</option><option value="background" disabled={!backdropReady}>Background</option>
            <option value="media" disabled={!recordingCues.length}>Media viewer</option></select></label>
          {recordingTarget === "background" && <label>Zoom <input aria-label="Background zoom" type="range" min="100" max="200"
            value={recordingBackground.value.zoom} onChange={event => recordingBackground.zoom(Number(event.target.value))} />{recordingBackground.value.zoom}%</label>}
          {recordingTarget === "media" && <>
            <label>Size <input aria-label="Media viewer size" type="range" min="25" max="85" value={recordingViewerSize}
              onChange={event => setRecordingViewerSize(Number(event.target.value))} />{recordingViewerSize}%</label>
            <label>Preview <select aria-label="Preview media" value={Math.min(recordingPreviewIndex, recordingCues.length - 1)}
              onChange={event => setRecordingPreviewIndex(Number(event.target.value))}>
              {recordingCues.map((cue, index) => <option key={`${index}:${cue.id}`} value={index}>{index + 1}. {cue.kind === "video" ? "Video poster" : "Image"}: {cue.alt}</option>)}
            </select></label>
          </>}
          <button ref={arrangementDone} type="button" className="recording-arrangement-done" onClick={finishRecordingArrangement}>Done arranging</button>
        </div>}
        <div className="stage-secondary-actions">
          <button className="stage-share-button" type="button" onClick={() => void share()}>
                  <svg
                    className="stage-share-button__laugh"
                    viewBox="0 0 38 28"
                    aria-hidden="true"
                    focusable="false"
                    shapeRendering="crispEdges"
                  >
                    <path fill="#42d8c9" d="M4 4h34v19H23l-7 5v-5H4z" />
                    <path fill="#07152f" d="M0 0h35v20H21l-8 6v-6H0z" />
                    <path fill="#eb5f91" d="M3 3h29v14H20l-4 3v-3H3z" />
                    <path fill="#fff0cf" d="M6 6h3v4h4V6h3v10h-3v-3H9v3H6zM19 8h2V6h5v2h2v8h-3v-3h-3v3h-3zm3 0v3h3V8zM30 6h2v7h-2zm0 8h2v2h-2z" />
                  </svg>
            <span>{shareLabel}</span>
          </button>
          <button
            ref={followButton}
            className="stage-follow-button"
            type="button"
            aria-haspopup="dialog"
            aria-expanded={socialsOpen}
            onClick={() => setSocialsOpen(true)}
          >
            <svg
              className="stage-follow-button__icon"
              viewBox="0 0 38 28"
              aria-hidden="true"
              focusable="false"
              shapeRendering="crispEdges"
            >
              <path fill="#42d8c9" d="M4 4h34v23H4z" />
              <path fill="#07152f" d="M0 0h35v24H0z" />
              <path fill="#eb5f91" d="M3 3h29v18H3z" />
              <path fill="#fff0cf" d="M7 5h8v8H7zM5 15h12v4H5zM22 5h4v4h4v4h-4v4h-4v-4h-4V9h4z" />
              <path fill="#07152f" d="M10 8h2v2h-2z" />
            </svg>
            <span>FOLLOW</span>
          </button>
        </div>

        {socialsOpen && (
          <div className="stage-social-backdrop" data-stage-modal>
            <button className="stage-social-scrim" type="button" tabIndex={-1} aria-label="Close social links" onClick={closeSocials} />
            <section ref={socialSheet} className="stage-social-tray" role="dialog" aria-modal="true" aria-labelledby="social-tray-title" tabIndex={-1}>
              <header>
                <div>
                  <div className="eyebrow">FIND THE AWKWARDNESS</div>
                  <h2 id="social-tray-title">Follow Generally Awkward</h2>
                </div>
                <button data-modal-close type="button" aria-label="Close social links" onClick={closeSocials}>CLOSE</button>
              </header>
              <nav aria-label="Generally Awkward social channels">
                {([
                  ["IG", "INSTAGRAM", "https://www.instagram.com/generally.awkward.shorts", "instagram"],
                  ["TT", "TIKTOK", "https://www.tiktok.com/@generally.awkward", "tiktok"],
                  ["FB", "FACEBOOK", "https://www.facebook.com/generallyawkwardshorts", "facebook"],
                  ["X", "X", "https://x.com/GenerallyAwkard", "x"],
                  ["R/", "REDDIT", "https://www.reddit.com/r/GenerallyAwkward", "reddit"],
                  ["CP", "CODEX PETS", "https://petdex.dev/u/generallyawkward", "codex_pets"],
                ] as const).map(([badge, label, href, metric]) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      setSocialsOpen(false);
                      void trackStageMetric(`social_${metric}_clicked`, episode.id);
                    }}
                  >
                    <span aria-hidden="true">{badge}</span>
                    <strong>{label}</strong>
                  </a>
                ))}
              </nav>
            </section>
          </div>
        )}

        <div ref={cameoStage} className={`studio-scene ${episode.lines.some(line => validatedVisuals(line).length > 0) ? "has-edition-visuals" : ""}`}>
        {recordMode && recordingViewerCustom ? <div className="recording-media-position"
          {...recordingPositions.bind("media-viewer", "Position media viewer", recordingTarget === "media")}>{visualScreen}</div> : visualScreen}
        {recordingArrangementOpen && recordingTarget === "background" && <div className="recording-background-position" {...recordingBackground.bind} />}
        {generatedBackdropUrl && (
          <img
            key={generatedBackdropUrl}
            className={`stage-backdrop ${backdropReady ? "is-ready" : ""}`}
            src={generatedBackdropUrl}
            alt=""
            aria-hidden="true"
            decoding="async"
            style={{
              objectPosition: recordMode ? recordBackgroundPosition : `${focusPercent(episode.backgroundFocusX, 50)}% ${focusPercent(episode.backgroundFocusY, 40)}%`,
            }}
            onLoad={() => setLoadedBackdropUrl(generatedBackdropUrl)}
            onError={() => {
              setLoadedBackdropUrl(null);
              setActiveBackdropUrl(null);
            }}
          />
        )}
        {generatedBackdropUrl && <BackgroundMotion key={`${featuredEpisode.id}:${featuredEpisode.assetHashes?.background ?? ""}`} episode={featuredEpisode} motionEnabled={castMotion.enabled && !recordingGreenScreen}
          objectPosition={recordMode ? recordBackgroundPosition : `${focusPercent(featuredEpisode.backgroundFocusX, 50)}% ${focusPercent(featuredEpisode.backgroundFocusY, 40)}%`} />}
        <div className="stage-lights" aria-hidden="true"><i /><i /><i /></div>
        <div className="stage-brand" aria-label="AwkwardPixels Studio"><span>AWKWARD</span><i>PIXELS<sup>TM</sup></i><small>STUDIO</small></div>
        <div className="stage-scenery" aria-hidden="true"><span /><span /><span /></div>
        <div className="on-air" aria-label={recordingBusy ? "On air" : "Standing by"}><span>ON</span><b>AIR</b></div>

        <div className="cast">
          <div ref={winstonActor} {...recordingPositions.bind("winston", "Position Winston", recordingTarget === "characters")} className={`actor winston ${speaker === "winston" ? "speaking" : ""}`}>
            <canvas ref={winstonCanvas} aria-label="Winston, a large golden doodle co-host" />
            <strong>WINSTON</strong>
          </div>
          <div ref={asherActor} {...recordingPositions.bind("asher", "Position Asher", recordingTarget === "characters")} className={`actor asher ${speaker === "asher" ? "speaking" : ""}`}>
            <canvas ref={asherCanvas} aria-label="Asher, a smaller mini Australian shepherd co-host" />
            <strong>ASHER</strong>
          </div>
        </div>
        </div>
        <div className="studio-control-deck" role="group" aria-label="Studio player controls" inert={recordingInterfaceHidden}>
        <div className="play-action-stack">
          {recordCaptain ? <button ref={playButton} className="play-button" type="button"
            aria-label={recordingCaptainPlaying ? "Stop Captain" : "Play Captain"}
            disabled={!ready || !recordingCaptainPlaying && (!captain.available || !selectedCaptainScript)}
            onClick={() => { if (recordingCaptainPlaying) stopPlayback(); else { void playCaptainRecording(); requestPhoneFullscreen(); } }}>
            <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false"><path d={recordingCaptainPlaying ? "M5 5h14v14H5z" : "M7 3v18l15-9z"} /></svg>
            <span className="play-button-label">{recordingCaptainPlaying ? "Stop Captain" : "Play Captain"}</span>
          </button> :
          <button ref={playButton} className="play-button" type="button" title={playing ? "Pause or resume playback. Escape stops playback." : undefined} aria-label={playing ? paused ? "RESUME" : "PAUSE" : showInteractionPending ? processingReplyLabel : ready ? featuredPresentation.playLabel : "LOADING CAST..."} onClick={() => { if (playbackLock.current) togglePause(); else { void play(featuredEpisode); requestPhoneFullscreen(); } }} disabled={!ready || captain.state === "active" || (!playing && showInteractionPending)}>
            {!ready || showInteractionPending && !playing ? <span aria-hidden="true">…</span> : (
              <svg aria-hidden="true" viewBox="0 0 24 24" focusable="false">
                <path d={playing && !paused ? "M6 4h4v16H6zM14 4h4v16h-4z" : "M7 3v18l15-9z"} />
              </svg>
            )}
            {!playing && <span className="play-button-label">{showInteractionPending ? processingReplyLabel : ready ? featuredPresentation.playLabel : "LOADING CAST..."}</span>}
          </button>}
          {!recordMode && captain.available && <button ref={captain.trigger} className="captain-button" type="button"
            data-reminder={captainReminderReady}
            onClick={() => captain.state === "idle" ? captain.request(playbackLock.current) : captain.dismiss()}
            aria-pressed={captain.state !== "idle"}
            title={captain.state === "idle" ? captain.buttonLabel : `Dismiss ${captain.displayName}`}
            aria-label={captain.state === "idle" ? `${captain.buttonLabel} Invite ${captain.displayName}` : `Dismiss ${captain.displayName}`}>
            <img src="/images/captain-emblem-flat-v2.svg" alt="" width="36" height="36" aria-hidden="true" />
          </button>}
          {captain.queueStatus && <span className="caption-sr-only" role="status">{captain.queueStatus}</span>}
        </div>
          {viewingPastEdition && !recordMode && (
            <a className="current-edition-shortcut" href="/" aria-label="Go to current edition">
              <span>Past edition</span>
              <strong>Go to current edition</strong>
            </a>
          )}
          {recordMode && <div className="recording-options">
            <label>Record <select aria-label="Recording mode" value={recordingScript} disabled={recordingBusy}
              onChange={event => {
                cancelCameo(); setRecordingScript(event.target.value); setRecordingArranging(false);
                const url = new URL(window.location.href);
                if (event.target.value === "captain") url.searchParams.set("performer", "captain");
                else url.searchParams.delete("performer");
                window.history.replaceState(null, "", url);
              }}>
              <option value="edition">Winston &amp; Asher</option><option value="captain">The Captain</option>
            </select></label>
            <label>Background <select aria-label="Recording background" value={recordingBackgroundStyle} disabled={recordingBusy}
              onChange={event => {
                setRecordingBackgroundStyle(event.target.value);
                if (recordingTarget === "background") setRecordingTarget("characters");
                const url = new URL(window.location.href);
                if (event.target.value === "green") url.searchParams.set("background", "green");
                else url.searchParams.delete("background");
                window.history.replaceState(null, "", url);
              }}>
              <option value="scene">Studio scene</option><option value="green">Green screen</option>
            </select></label>
            {recordCaptain && <>
              <label>Size <input aria-label="Captain size" type="range" min="50" max="150" step="5"
                value={recordingCaptainSize} disabled={recordingBusy}
                onChange={event => setRecordingCaptainSize(Number(event.target.value))} />
                <output>{recordingCaptainSize}%</output></label>
              {captain.available && selectedCaptainScript ? <>
                <label>Script <select aria-label="Captain script" value={selectedCaptainScript.id} disabled={recordingBusy}
                  onChange={event => setRecordingCaptainScript(event.target.value)}>
                  {captain.scripts.map((script, index) => <option key={script.id} value={script.id}>Script {index + 1}</option>)}
                </select></label>
                <details className="recording-script-preview"><summary>View script</summary><p>{selectedCaptainScript.text}</p></details>
              </> : <p role="status">{!ready || captain.loading ? "Loading Captain scripts…" : "No prepared Captain script is available for this edition. Choose another edition or switch to Winston & Asher."}</p>}
              <p>{cameoReducedMotion || !captain.comic ? "This take uses quiet staging." : "Play includes the fly-in, script and fly-out."}</p>
            </>}
            <label><input type="checkbox" checked={hideRecordingControls}
              onChange={event => setHideRecordingControls(event.target.checked)} />
              Hide controls during playback</label>
            <p>Tap the scene to show controls; Escape stops playback.</p>
            {!recordCaptain && <><p id="recording-placement-help">Arrange the scene before Play. Drag the selected item, or use Tab + arrow keys. Zoom the background to pan both ways.</p>
            <div className="recording-position-actions">
            <button ref={arrangeButton} className="recording-reset" type="button" disabled={!recordingPositions.editable}
              onClick={() => { setRecordingArranging(true); requestAnimationFrame(() => arrangementDone.current?.focus({ preventScroll: true })); }}>Arrange scene</button>
            <button className="recording-reset" type="button" onClick={() => { recordingPositions.reset(); recordingBackground.reset(); setRecordingViewerCustom(false); setRecordingViewerSize(50); setRecordingTarget("characters"); }}
              disabled={!recordingPositions.editable || !(recordingPositions.changed || recordingBackground.changed || recordingViewerCustom)}>Reset layout</button>
            </div></>}
          </div>}
        {!recordMode && <button type="button" className="studio-caption-toggle"
          aria-label={captionsVisible ? "Hide captions" : "Show captions"}
          title={captionsVisible ? "Hide captions" : "Show captions"}
          aria-controls="studio-live-captions" aria-expanded={captionsVisible}
          disabled={showInteractionPending}
          onClick={() => setCaptionsVisible(visible => !visible)}>
          <span className="studio-caption-label" aria-hidden="true">{captionsVisible ? "Hide captions" : "Show captions"}</span>
          <span className="studio-caption-symbol" aria-hidden="true">CC</span>
        </button>}
        {!recordMode && <a className="studio-archive-link" href="/stories" aria-label="Past editions" title="Past editions">
          <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path fill="currentColor" d="M2 2h10v2H4v8H2zm4 4h8v8H6zm2 2v4h4V8z" /></svg>
          <span>Past editions</span>
        </a>}
        {!recordMode && <a className="studio-character-link" href="/characters/" aria-label="Meet the characters" title="Meet the characters">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <circle cx="9" cy="8" r="3" /><path d="M3 20v-2a6 6 0 0 1 12 0v2M16 5a3 3 0 0 1 0 6M17 14a5 5 0 0 1 4 5v1" />
          </svg>
          <span>Meet the characters</span>
        </a>}
        {!recordMode && <div className="studio-cta-actions" data-promoted={studioCtaVisible ? "true" : undefined} role="group" aria-label="Support AwkwardPixels Studio">
          {availability?.capabilities.share && <button type="button" className="studio-cta-share" title="Share studio" data-feedback={shareLabel !== "SHARE" ? "true" : undefined} aria-label={shareLabel === "SHARE" ? "Share studio" : shareLabel} onClick={() => void share(true)}>
            <img src="/images/studio-share-pixel-v1.png" width="28" height="28" alt="" aria-hidden="true" decoding="async" />
            <span>{shareLabel === "SHARE" ? "Share" : shareLabel === "LINK COPIED" ? "Copied!" : shareLabel}</span>
          </button>}
          {tipEnabled && <button type="button" className="studio-cta-tip" aria-label="Tip Jar" title="Tip Jar" aria-haspopup="dialog" onClick={(event) => openTip(event.currentTarget)}>
            <img src="/images/audience-token-jar-heart-v1.png" width="28" height="28" alt="" aria-hidden="true" decoding="async" />
            <span>Tip Jar</span>
          </button>}
        </div>}
        {captain.actor}
        </div>

        <div ref={captionDeck} inert={recordingInterfaceHidden} className={`caption-deck ${!captain.caption && !playing && storyCard && !recordMode && !showInteractionPending ? "has-story-card" : ""} ${showInteractionPending ? "is-awaiting-response" : ""}`}>
          <div className="studio-dialogue">
          {showInteractionPending ? (
            <div className="stage-response-wait" role="status" aria-live="polite" aria-busy="true">
              <span className="stage-response-wait__signal" aria-hidden="true"><i /><i /><i /></span>
              <div className="stage-response-wait__copy">
                <p className="stage-response-wait__title">Your message made it backstage</p>
                <p className="stage-response-wait__detail">Winston and Asher are thinking it over. Their reply may take a minute.</p>
              </div>
            </div>
          ) : null}
            <div id="studio-live-captions" className="studio-caption-body"
              hidden={!recordMode && (!captionsVisible || showInteractionPending || !captain.caption && !playing && !!storyCard)}>
              {(recordMode || captionsVisible) && (captain.caption || playing || recordMode || !storyCard) && <>
              {recordCaptain ? <small>{captain.displayName} · {featuredEpisode.title}</small> : recordMode ? <small>{presentation.label ? `${presentation.label} · ` : ""}{episode.title}</small> :
                <small className="studio-caption-speaker">{captain.caption?.displayName ?? (speaker === "asher" ? "Asher" : speaker === "winston" ? "Winston" : "Studio")}</small>}
              <StageCaption text={captain.caption?.text ?? (recordCaptain ? selectedCaptainScript?.text ?? "Choose a prepared Captain script to record." : subtitle)} playback={captain.caption ? captain.caption.playback : playing && captionPlayback?.line.text === subtitle ? captionPlayback : undefined} />
              </>}
            </div>
          <div className="caption-status">
            <p className={`playback-notice ${paused && !audioFailure ? "is-pause-only" : ""}`} role="status" aria-live="polite" aria-atomic="true">
              {[captain.caption ? captain.caption.note : audioFailure, paused ? "Playback paused." : null].filter(Boolean).join(" ")}
            </p>

          </div>
          </div>
          {!recordMode && <div className="studio-episode-heading">
          {storyCard ? (
            <div className="stage-story-card">
              <div className="stage-story-card__copy">
                <small>{featuredPresentation.label ? `${featuredPresentation.label} · ` : ""}FROM {storyCard.publisher}</small>
                <h1>{storyCard.headline}</h1>
              </div>
            </div>
          ) : <h1>{featuredEpisode.title}</h1>}
          {featuredPresentation.details && <p className="episode-presentation-details">{featuredPresentation.details}</p>}
          </div>}
          {!recordMode && (editionBrief || editionLinks.length > 0) && <section className="studio-edition-brief" aria-label="Edition brief">
            {editionBrief && <p>{editionBrief}</p>}
            {editionLinks.map(link => <p className="studio-edition-resource" key={link.url}><a href={link.url} target="_blank" rel="noopener noreferrer">{link.label}</a></p>)}
          </section>}
          <div className="caption-meta">
            {!recordMode && storyCard && <button
              ref={storyCardButton}
              className="studio-source-action"
              type="button"
              aria-haspopup="dialog" aria-controls="studio-source-dialog"
              onClick={(event) => openStory(event.currentTarget)}
              aria-label={`Read source: ${storyCard.headline}`}
            >
              Read source <span aria-hidden="true">&#8599;</span>
            </button>}
            {recordMode && <p className="stage-ai-disclaimer">AI-generated. Information may be incorrect and does not represent the views of Intrigued.me, LLC.</p>}
            {!playing && !recordMode && (audienceAvailable || showInteractionPending) && <button ref={audienceButton} className="audience-entry-button" type="button" aria-label="Talk to Winston and Asher" aria-haspopup="dialog" onClick={(event) => openAudience(event.currentTarget)} disabled={showInteractionPending}>
              <span>{showInteractionPending ? "The hosts are thinking…" : "Say hello"}</span>
            </button>}
            {recordMode ? <details className="transcript-drawer">
              <summary>TRANSCRIPT</summary>
              {transcriptContent}
            </details> : <button ref={transcriptButton} className="studio-transcript-action" type="button"
              aria-haspopup="dialog" aria-controls="studio-transcript-dialog"
              onClick={() => { setSocialsOpen(false); transcriptDialog.current?.showModal(); }}>
              Sources & transcript
            </button>}
          </div>
        </div>

        {effect === "rickroll" && (
          <div className="easter-player">
            <button type="button" onClick={() => setEffect(null)} aria-label="Close surprise video">CLOSE TRANSMISSION</button>
            <iframe
              title="A surprise studio transmission"
              src="https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?autoplay=1"
              allow="autoplay; encrypted-media; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </div>
        )}
      </section>

      <div ref={stageNotices} className="stage-notices">
        {import.meta.env.DEV && import.meta.env.VITE_ANIMATION_REVIEW === "1" &&
          <aside className="creator-review-notice">Local creator review — visual-only timing; not alignment qualification. Not deployed.</aside>}
        {castMotion.disabledBySystem && <div className="cast-motion-notice" role="status">
          <svg className="cast-motion-notice__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true" focusable="false">
            <circle cx="12" cy="12" r="9" /><path d="M9 8v8M15 8v8" />
          </svg>
          <span className="cast-motion-notice__copy"><strong>Reduced motion is on</strong><span>Following your device setting.</span></span>
          <button type="button" onClick={() => { stopPlayback(); castMotion.setEnabled(true); }}>Enable animations</button>
        </div>}
        {!recordMode && <section className="studio-info" aria-label="More from the studio">
          <nav className="studio-links" aria-label="Studio channels and technology">
            <a className="youtube-channel-link" href="https://www.youtube.com/@generallyawkward.shorts/shorts"
              target="_blank" rel="noopener noreferrer" aria-label="Watch more Generally Awkward on YouTube">
              <span className="youtube-channel-link__play" aria-hidden="true" />
              <span className="youtube-channel-link__copy"><span className="youtube-channel-link__lead">Watch more on YouTube</span><span className="youtube-channel-link__name">Generally Awkward</span></span>
            </a>
            <a className="stage-powered-by" href="https://try.elevenlabs.io/76ihftbj3qpv"
              target="_blank" rel="sponsored noopener noreferrer"
              aria-label="Powered by ElevenLabs (affiliate link; we may earn a commission)"
              onClick={() => void trackStageMetric("elevenlabs_clicked", featuredEpisode.id)}>
              <svg className="stage-powered-by__mark" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M5 3h5v18H5zM14 3h5v18h-5z" /></svg>
              <span className="stage-powered-by__copy"><span>Powered by</span><strong>ElevenLabs</strong><small className="stage-powered-by__disclosure">Affiliate Link</small></span>
            </a>
          </nav>
        </section>}
      </div>
      <div className="studio-reporting-notes">
        {!recordMode && !audioFailure && (activeNewsNotice || activeReportingBasis) && (
          <button ref={reportingButton} className="stage-disclosure-control" type="button"
            aria-label="Open reporting details" aria-haspopup="dialog" aria-controls="stage-reporting-dialog"
            onClick={() => { if (reportingDialog.current && !reportingDialog.current.open) reportingDialog.current.showModal(); }}>
            {activeNewsNotice ? "DEVELOPING STORY" : "REPORTING NOTE"} · DETAILS
          </button>
        )}
      </div>
      {recordMode ? legalDock : <footer className="studio-ai-notice" aria-label="Studio disclosures and legal links">
        <p className="stage-ai-disclaimer">AI-generated. Information may be incorrect and does not represent the views of Intrigued.me, LLC.</p>
        {legalDock}
      </footer>}

      {!recordMode && <dialog ref={transcriptDialog} id="studio-transcript-dialog" className="studio-transcript-dialog"
        aria-labelledby="studio-transcript-title" onClose={() => transcriptButton.current?.focus({ preventScroll: true })}>
        <header><h2 id="studio-transcript-title">Sources & transcript</h2>
          <button type="button" onClick={() => transcriptDialog.current?.close()} autoFocus>Close</button></header>
        {transcriptContent}
      </dialog>}

      <dialog ref={reportingDialog} id="stage-reporting-dialog" className="stage-reporting-dialog"
        aria-labelledby="stage-reporting-title" onClose={() => reportingButton.current?.focus({ preventScroll: true })}>
        <h2 id="stage-reporting-title">Reporting details</h2>
        <NewsDisclosures notice={activeNewsNotice} basis={activeReportingBasis} />
        <button type="button" onClick={() => reportingDialog.current?.close()}>CLOSE</button>
      </dialog>

      {selectionError && (
        <aside className="episode-presentation-summary" aria-label="Edition availability">
          <p role="status">{selectionError} <a href="/stories">Browse Stories</a></p>
        </aside>
      )}

      {featuredEpisode.sources.length > 0 && (
        <details className="sources">
          <summary>THE RECEIPTS ({featuredEpisode.sources.length})</summary>
          <ul>{featuredEpisode.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noreferrer">{source.title}</a></li>)}</ul>
        </details>
      )}

      <div className="share-strip">
        <span>Fresh in the morning. Recut after the day has something to say.</span>
        <nav className="clip-views" aria-label="Social recording views">
          <b>CLIP VIEW</b>
          <a href={recordHref("portrait", qaBackdropUrl)} target="_blank" rel="noreferrer">9:16</a>
          <a href={recordHref("square", qaBackdropUrl)} target="_blank" rel="noreferrer">1:1</a>
          <a href={recordHref("landscape", qaBackdropUrl)} target="_blank" rel="noreferrer">16:9</a>
        </nav>
      </div>

      {audienceOpen && (
        <div className="stage-audience-backdrop" data-stage-modal>
          <button className="stage-audience-scrim" type="button" aria-label="Close audience panel" onClick={closeAudience} tabIndex={-1} />
          <section
            ref={audienceSheet}
            className="stage-audience-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="audience-sheet-title"
            tabIndex={-1}
          >
            <header>
              <div>
                <div className="eyebrow">WINSTON &amp; ASHER ARE LISTENING</div>
                <h2 id="audience-sheet-title">{audienceAvailable ? "What would you like to tell them?" : "Messages are unavailable"}</h2>
                <p className="audience-intro">{audienceAvailable ? "Winston and Asher are fictional AI characters, not people. Replies may be wrong and are not professional or emergency support. Ask a question or share a thought; responses depend on availability and your daily message limit." : "You can still watch the edition and read its transcript. Please check back for conversations."}</p>
              </div>
              <button data-modal-close className="stage-audience-close" type="button" onClick={closeAudience}>CLOSE</button>
            </header>

            {storyCard && (
              <button
                className="audience-story-context"
                type="button"
                onClick={(event) => {
                  setAudienceOpen(false);
                  openStory(event.currentTarget);
                }}
              >
                {displayedStoryCardImageUrl ? <img key={storyImageBinding} src={displayedStoryCardImageUrl} alt="" onError={onStoryImageError} /> : <span aria-hidden="true">AP</span>}
                <span>
                  <small>{replayStory ? "THIS REPLAY'S STORY" : "THIS EDITION'S STORY"}</small>
                  <strong>{storyCard.headline}</strong>
                </span>
              </button>
            )}

            {(signInAvailable || previewAudienceControls) && (
              <section className="audience-desk audience-conversation" aria-label="Message Winston and Asher">
                {!signedIn && !previewAudienceControls && (
                  <div className="audience-sign-in">
                    <p>{effectivePromptEnabled ? "Sign in to send Winston and Asher one message today." : "Sign in to view account options. Messaging is currently unavailable."}</p>
                    <a className="google-button" href={signInHref}>SIGN IN WITH GOOGLE</a>
                  </div>
                )}

                {(signedIn || previewAudienceControls) && (
                  <form onSubmit={(event) => void submit(event)}>
                    {messageStatus && <p className="message-status" role="status" aria-live="polite">{messageStatus}</p>}
                    {displayedAllowance?.remaining === 0 ? (
                      <div className="allowance-empty" role="status">
                        <strong>THEY&apos;VE HEARD FROM YOU TODAY</strong>
                        <p>A completed share can raise today&apos;s limit to two messages. Otherwise, Winston and Asher will be listening again tomorrow.</p>
                      </div>
                    ) : (
                      <>
                        <label htmlFor="audience-message">Your message to Winston and Asher</label>
                        {displayedAllowance && (
                          <p className="allowance-status">
                            {displayedAllowance.remaining === 1
                              ? "You can send Winston and Asher 1 message today."
                              : `You can send Winston and Asher ${displayedAllowance.remaining} messages today.`}
                          </p>
                        )}
                        <div className={`input-frame ${rejected ? "rejected" : ""}`}>
                          <textarea
                            id="audience-message"
                            value={viewerText}
                            onChange={(event) => setViewerText(event.target.value.replace(/[\r\n]+/g, " "))}
                            maxLength={240}
                            rows={5}
                            placeholder={effectivePromptEnabled ? "Winston and Asher, why do dogs always want the warm spot?" : "Winston and Asher are between conversations."}
                            disabled={!effectivePromptEnabled || submitting}
                          />
                          <span>{viewerText.length}/240</span>
                        </div>
                        <div className="age-check">
                          <input id="age-and-terms" type="checkbox" checked={ageAttested} onChange={(event) => setAgeAttested(event.target.checked)} />
                          <label htmlFor="age-and-terms">I am at least 13 and agree to the <a href="/terms/" target="_blank" rel="noreferrer">Terms</a> and acknowledge the <a href="/privacy/" target="_blank" rel="noreferrer">Privacy Notice</a>.</label>
                        </div>
                        <button type="submit" disabled={!effectivePromptEnabled || submitting || !ageAttested || !viewerText.trim()}>
                          {submitting ? "SENDING TO THE WINGS..." : "SEND TO WINSTON & ASHER"}
                        </button>
                      </>
                    )}
                    <aside className={`share-unlock ${displayedAllowance?.shareUnlocked ? "unlocked" : ""}`}>
                      <div>
                        <strong>{displayedAllowance?.shareUnlocked ? "TWO MESSAGES ARE READY TODAY" : "SHARE THE REEL FOR A SECOND MESSAGE"}</strong>
                        <p>
                          {displayedAllowance?.shareUnlocked
                            ? "Thanks for sharing. You can send Winston and Asher up to two messages today."
                            : "share" in navigator
                              ? "Complete one native share to raise today's limit from one message to two."
                              : "This browser can copy the reel link, but it cannot confirm a completed share."}
                        </p>
                      </div>
                      {!displayedAllowance?.shareUnlocked && "share" in navigator && (
                        <div className="share-unlock-actions">
                          <button type="button" onClick={() => void share()}>SHARE FOR A SECOND MESSAGE</button>
                        </div>
                      )}
                    </aside>
                  </form>
                )}
              </section>
            )}

            {!signInAvailable && !previewAudienceControls && (
              <section className="audience-desk soft-closed" role="status">
                Winston and Asher aren&apos;t taking messages right now. They&apos;re probably blaming the clipboard. Please try again soon.
              </section>
            )}
          </section>
        </div>
      )}

      {tipOpen && (
        <div className="stage-audience-backdrop studio-tip-backdrop" data-stage-modal>
          <button className="stage-audience-scrim" type="button" aria-label="Close tip jar" onClick={closeTip} tabIndex={-1} />
          <section ref={tipSheet} className="stage-audience-sheet studio-tip-sheet" role="dialog" aria-modal="true" aria-labelledby="tip-title" tabIndex={-1}>
            <header>
              <div>
                <div className="eyebrow">TOKEN TIP JAR</div>
                <h2 id="tip-title">Help us raise the curtain.</h2>
                <p className="audience-intro">A little support for more character moments, places to explore, and surprises waiting in the wings.</p>
              </div>
              <button data-modal-close className="stage-audience-close" type="button" onClick={closeTip}>CLOSE</button>
            </header>
            <section className="tip-jar" aria-label="Choose a tip amount">
              <img className="tip-jar-icon" src="/images/audience-token-jar.png" alt="" aria-hidden="true" />
              <div>
                <p>No studio sign-in needed. Adult payers can choose a one-time USD tip. <a href="/terms/#refunds" target="_blank" rel="noopener noreferrer">14-day refund policy</a>.</p>
                {signedIn && audienceAvailable && <p>A completed tip raises your signed-in account&apos;s limit to three messages today.</p>}
                <div className="tip-buttons" aria-busy={tipSubmitting}>
                  {[2, 4, 6, 10].map((amount) => <button key={amount} type="button" disabled={!tipEnabled || tipSubmitting} onClick={() => void tip(amount)}>${amount}</button>)}
                  <button type="button" disabled={!tipEnabled || tipSubmitting} aria-expanded={customTipOpen} aria-controls="custom-tip-form" onClick={() => { setCustomTipOpen(!customTipOpen); setCustomTipError(""); }}>Other amount</button>
                </div>
                {customTipOpen && (
                  <form id="custom-tip-form" className="tip-custom-form" noValidate onSubmit={(event) => {
                    event.preventDefault();
                    const amount = parseTipAmount(customTipAmount);
                    if (amount === null) {
                      setCustomTipError("Enter an amount from $2 to $100, with no more than two decimal places.");
                      customTipInput.current?.focus();
                      return;
                    }
                    setCustomTipError("");
                    void tip(amount);
                  }}>
                    <label htmlFor="custom-tip-amount">Amount (USD)</label>
                    <div className="tip-custom-fields">
                      <input ref={customTipInput} id="custom-tip-amount" type="text" inputMode="decimal" autoComplete="off" maxLength={12} value={customTipAmount} disabled={!tipEnabled || tipSubmitting} aria-invalid={!!customTipError} aria-describedby={`custom-tip-help${customTipError ? " custom-tip-error" : ""}`} onChange={(event) => { setCustomTipAmount(event.target.value); setCustomTipError(""); }} />
                      <button type="submit" disabled={!tipEnabled || tipSubmitting}>{tipSubmitting ? "Opening PayPal…" : "Continue to PayPal"}</button>
                    </div>
                    <p id="custom-tip-help">$2–$100 USD. Up to two decimal places.</p>
                    {customTipError && <p id="custom-tip-error" role="alert">{customTipError}</p>}
                  </form>
                )}
                <p className="legal-note">Tips support development. They do not guarantee a response, feature, or uninterrupted service, and they are not charitable donations. By continuing, you agree to the <a href="/terms/" target="_blank" rel="noreferrer">payment terms</a>.</p>
                {!tipEnabled && <p role="status">The tip jar is closed right now. No payment will be started.</p>}
                {tipStatus && <p className="tip-status" role="status" aria-live="polite">{tipStatus}</p>}
              </div>
            </section>
          </section>
        </div>
      )}

      {storyOpen && storyCard && (
        <div className="stage-story-backdrop" data-stage-modal>
          <button className="stage-story-scrim" type="button" tabIndex={-1} aria-label="Close story" onClick={closeStory} />
          <section ref={storySheet} id="studio-source-dialog" className="stage-story-sheet" role="dialog" aria-modal="true" aria-labelledby="stage-story-title" tabIndex={-1}>
            <header>
              <div>
                <div className="eyebrow">{replayStory ? "THIS REPLAY'S ORIGINAL STORY" : "THIS EDITION'S ORIGINAL STORY"}</div>
                <span>{storyCard.publisher}{storyCard.publishedOn ? ` · ${formatStoryDate(storyCard.publishedOn)}` : ""}</span>
              </div>
              <button data-modal-close type="button" onClick={closeStory}>CLOSE</button>
            </header>
            {displayedStoryCardImageUrl ? (
              <img key={storyImageBinding} className="stage-story-sheet__image" src={displayedStoryCardImageUrl} alt="" onError={onStoryImageError} />
            ) : (
              <div className="stage-story-sheet__placeholder" aria-hidden="true">NEWS PHOTO UNAVAILABLE</div>
            )}
            <h2 id="stage-story-title">{storyCard.headline}</h2>
            <NewsDisclosures notice={featuredNewsNotice} basis={featuredReportingBasis} />
            <a className="stage-story-sheet__primary" href={storyCard.url} target="_blank" rel="external noopener noreferrer">
              READ THE ORIGINAL STORY &#8599;
            </a>
            {featuredEpisode.sources.length > 1 && (
              <div className="stage-story-sheet__sources">
                <strong>MORE REPORTING</strong>
                {featuredEpisode.sources.slice(1).map((source) => (
                  <a key={source.url} href={source.url} target="_blank" rel="external noopener noreferrer">{source.title}</a>
                ))}
              </div>
            )}
          </section>
        </div>
      )}

      <footer>
        <div><b>AWKWARDPIXELS™ STUDIO</b><span>© 2026 Intrigued.me, LLC. All rights reserved.</span></div>
        <nav aria-label="Technology providers">
          <span>POWERED, IN PART, BY</span>
          <a href={import.meta.env.VITE_OPENAI_LINK ?? "https://openai.com/"} target="_blank" rel="sponsored noreferrer" className="provider openai">OpenAI</a>
          <a href={import.meta.env.VITE_ELEVENLABS_LINK ?? "https://elevenlabs.io/"} target="_blank" rel="sponsored noreferrer" className="provider eleven">ElevenLabs</a>
          <span>We may earn a commission from marked provider links.</span>
        </nav>
      </footer>

      {careOpen && (
        <div className="care-backdrop" role="presentation" data-stage-modal>
          <section ref={careSheet} className="care-card" role="dialog" aria-modal="true" aria-labelledby="care-title" tabIndex={-1}>
            <div className="care-signal">WE CARE ABOUT YOU</div>
            <h2 id="care-title">Let’s get a real person in your corner.</h2>
            <p>If you may act on thoughts of harming yourself or are in immediate danger, call local emergency services now. You deserve support from a trained human, not a comedy dog studio.</p>
            <div className="care-links">
              <a href="tel:988">U.S.: CALL OR TEXT 988</a>
              <a href="https://988.ca/" target="_blank" rel="noreferrer">CANADA: CALL OR TEXT 988</a>
              <a href="https://findahelpline.com/" target="_blank" rel="noreferrer">FIND HELP IN YOUR COUNTRY</a>
            </div>
            <button data-modal-close type="button" onClick={closeCare}>CLOSE</button>
          </section>
        </div>
      )}
    </main>
  );
}

async function loadFallbackCatalog(url: string): Promise<EpisodeManifest[]> {
  try {
    const raw = await stageFetch<unknown>(url, undefined, 6000);
    const root = raw as Record<string, unknown>;
    const candidates = (root.performances ?? root.episodes ?? root.scripts ?? []) as Array<Record<string, unknown>>;
    const normalized = candidates.map((item, index) => normalizeFallback(item, index)).filter(Boolean) as EpisodeManifest[];
    return normalized.length > 0 ? normalized : [hardFallback];
  } catch {
    return [hardFallback];
  }
}

function normalizeFallback(item: Record<string, unknown>, index: number): EpisodeManifest | null {
  const lines = Array.isArray(item.lines) ? item.lines : Array.isArray(item.script) ? item.script : [];
  const normalizedLines = lines.map((value) => {
    const line = value as Record<string, unknown>;
    const speaker = String(line.speaker ?? "").toLowerCase();
    if (speaker !== "winston" && speaker !== "asher") return null;
    return {
      speaker,
      text: String(line.text ?? ""),
      audio: typeof line.audio === "string" ? line.audio : undefined,
      audioSha256: typeof line.audioSha256 === "string" ? line.audioSha256 : undefined,
      requestedTtsAssetId: line.requestedTtsAssetId as EpisodeLine["requestedTtsAssetId"],
      servedTtsAssetId: line.servedTtsAssetId as EpisodeLine["servedTtsAssetId"],
      ttsPrivateManifestSha256: line.ttsPrivateManifestSha256 as EpisodeLine["ttsPrivateManifestSha256"],
      ttsDeliveryOutcome: line.ttsDeliveryOutcome as EpisodeLine["ttsDeliveryOutcome"],
      animationReview: line.animationReview as EpisodeLine["animationReview"],
      blinkStartsMs: line.blinkStartsMs as number[] | undefined,
      alignmentReceipt: line.alignmentReceipt as EpisodeLine["alignmentReceipt"],
      performanceReceipt: line.performanceReceipt as EpisodeLine["performanceReceipt"],
      expressions: line.expressions as EpisodeLine["expressions"],
      captionTiming: line.captionTiming as EpisodeLine["captionTiming"],
      durationMs: Number(line.durationMs ?? line.duration_ms ?? 2200),
      timeline: normalizeTimeline(line.timeline),
      gestures: normalizeGestures(line.gestures ?? line.poseEvents),
    } satisfies EpisodeLine;
  }).filter(Boolean) as EpisodeLine[];
  if (normalizedLines.length === 0) return null;
  return {
    id: String(item.id ?? `fallback-${index}`),
    title: String(item.title ?? item.name ?? "Studio Encore"),
    slot: "encore",
    publishedAt: new Date(0).toISOString(),
    validUntil: new Date(8640000000000000).toISOString(),
    backgroundUrl: String(item.backgroundUrl ?? "theme:newsroom"),

    sources: [],
    lines: normalizedLines,
    effect: typeof item.effect === "string" ? item.effect : undefined,
  };
}

function normalizeTimeline(value: unknown): EpisodeLine["timeline"] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const frame = entry as Record<string, unknown>;
    return { atMs: Number(frame.atMs ?? frame.at_ms ?? 0), frame: String(frame.frame ?? frame.viseme ?? "rest").toLowerCase() };
  });
}

function normalizeGestures(value: unknown): EpisodeLine["gestures"] {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => {
    const cue = entry as Record<string, unknown>;
    return {
      atMs: Number(cue.atMs ?? cue.at_ms ?? 0),
      durationMs: Number(cue.durationMs ?? cue.duration_ms ?? 700),
      pose: String(cue.pose ?? cue.gesture ?? "neutral"),
    };
  });
}

function safeBackdropUrl(value?: string): string | null {
  if (!value || value.startsWith("theme:")) return null;
  try {
    const url = new URL(value);
    const fileName = url.pathname.split("/").at(-1) ?? "";
    const validFile = /^[a-f0-9-]+\.jpg$/.test(fileName);
    const production =
      url.protocol === "https:" &&
      /^[a-z0-9-]+\.blob\.core\.windows\.net$/.test(url.hostname) &&
      url.pathname.startsWith("/stage-backdrops/");
    const development =
      import.meta.env.DEV &&
      (url.hostname === "127.0.0.1" || url.hostname === "localhost") &&
      url.pathname.includes("/stage-backdrops/");
    return validFile && (production || development) ? url.toString() : null;
  } catch {
    return null;
  }
}

function storyCardFor(episode: EpisodeManifest): NonNullable<EpisodeManifest["storyCard"]> | null {
  if (episode.explainerCoverage) return null; // An explainer is not a single publisher's breaking story.
  if (episode.storyCard) return episode.storyCard;
  const source = episode.sources[0];
  if (!source) return null;
  try {
    const host = new URL(source.url).hostname.replace(/^www\./i, "");
    return {
      headline: source.title,
      publisher: host,
      url: source.url,
      publishedOn: source.publishedOn,
    };
  } catch {
    return null;
  }
}

function safeStoryImageUrl(value?: string): string | null {
  if (typeof value !== "string" || !value.startsWith("/api/story-image/")) return null;
  const token = value.slice("/api/story-image/".length);
  return /^[A-Za-z0-9_-]+$/.test(token) ? value : null;
}

function formatStoryDate(value: string): string {
  const date = new Date(`${value}T12:00:00Z`);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(date);
}

function focusPercent(value: number | undefined, fallback: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.min(100, Math.max(0, value * 100));
}

function recordHref(mode: "portrait" | "square" | "landscape", qaBackdropUrl?: string): string {
  const current = new URLSearchParams(window.location.search);
  const route = {
    portrait: "/?record=portrait",
    square: "/?record=square",
    landscape: "/?record=landscape",
  }[mode];
  const target = new URL(route, window.location.origin);
  const share = current.get("share");
  if (share) target.searchParams.set("share", share);
  if (import.meta.env.DEV && qaBackdropUrl) target.searchParams.set("qaBackdrop", qaBackdropUrl);
  return `${target.pathname}${target.search}`;
}

function allowedEffect(value?: string): string | null {
  return value && ["lights-out", "rickroll", "confetti", "weather"].includes(value) ? value : null;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isLocalCareConcern(text: string): boolean {
  const value = text.toLowerCase();
  return [
    "kill myself",
    "end my life",
    "want to die",
    "suicide",
    "hurt myself",
    "self harm",
    "not worth living",
  ].some((phrase) => value.includes(phrase));
}
